import React, { useState, useEffect, useCallback } from 'react';
import { getPrompts, createPrompt, updatePrompt, deletePrompt } from '../services/promptService';
import { getProductDetails } from '../services/productDetailsService';
import './Prompts.css';

const Prompts = ({ featureGroupId, userRole }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [productDetails, setProductDetails] = useState([]);
  const [formData, setFormData] = useState({ 
    name: '', 
    prompt_text: '', 
    scope: 'Feature Map',
    live: false,
    context_data: []
  });

  const isOwner = userRole === 'Owner';

  const CONTEXT_OPTIONS = [
    'Full hierarchy feature name, description, details',
    'Persona tag definitions and data',
    'Feature Map description'
  ];

  const PRODUCT_DETAILS_PREFIX = 'Product Details: ';

  const loadPrompts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPrompts(featureGroupId);
      setPrompts(data);
    } catch (err) {
      setError(err.message || 'Failed to load prompts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId]);

  const loadProductDetails = useCallback(async () => {
    try {
      const data = await getProductDetails(featureGroupId);
      setProductDetails(data || []);
    } catch (err) {
      console.error('Error loading product details:', err);
      setProductDetails([]);
    }
  }, [featureGroupId]);

  useEffect(() => {
    if (featureGroupId) {
      loadPrompts();
      loadProductDetails();
    }
  }, [featureGroupId, loadPrompts, loadProductDetails]);

  const handleCreate = () => {
    setEditingPrompt(null);
    setFormData({ 
      name: '', 
      prompt_text: '',
      scope: 'Feature Map',
      live: false,
      context_data: []
    });
    setShowEditDialog(true);
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    let contextData = prompt.context_data || [];
    
    // Migrate legacy "Product Details" to individual document types
    if (contextData.includes('Product Details')) {
      // Remove legacy "Product Details"
      contextData = contextData.filter(item => item !== 'Product Details');
      
      // Add all available product details for this feature group
      productDetails.forEach(pd => {
        const optionValue = `${PRODUCT_DETAILS_PREFIX}${pd.document_type}`;
        if (!contextData.includes(optionValue)) {
          contextData.push(optionValue);
        }
      });
    }
    
    setFormData({
      name: prompt.name || '',
      prompt_text: prompt.prompt_text || '',
      scope: prompt.scope || 'Feature Map',
      live: prompt.live || false,
      context_data: contextData
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deletePrompt(promptId);
      await loadPrompts();
    } catch (err) {
      setError(err.message || 'Failed to delete prompt');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Prompt name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (editingPrompt) {
        await updatePrompt(editingPrompt.id, formData);
      } else {
        await createPrompt({
          ...formData,
          feature_group_id: featureGroupId
        });
      }
      
      setShowEditDialog(false);
      await loadPrompts();
    } catch (err) {
      setError(err.message || `Failed to ${editingPrompt ? 'update' : 'create'} prompt`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowEditDialog(false);
    setEditingPrompt(null);
    setFormData({ 
      name: '', 
      prompt_text: '',
      scope: 'Feature Map',
      live: false,
      context_data: []
    });
    setError(null);
  };

  const handleContextToggle = (contextOption) => {
    setFormData(prev => {
      const currentContext = prev.context_data || [];
      const newContext = currentContext.includes(contextOption)
        ? currentContext.filter(c => c !== contextOption)
        : [...currentContext, contextOption];
      return { ...prev, context_data: newContext };
    });
  };

  const handleExport = () => {
    const exportData = {
      name: formData.name,
      prompt_text: formData.prompt_text,
      scope: formData.scope,
      live: formData.live,
      context_data: formData.context_data
    };
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_prompt.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const handleImportFromList = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const importedData = JSON.parse(event.target.result);
          setFormData({
            name: importedData.name || '',
            prompt_text: importedData.prompt_text || '',
            scope: importedData.scope || 'Feature Map',
            live: importedData.live !== undefined ? importedData.live : false,
            context_data: importedData.context_data || []
          });
          setEditingPrompt(null);
          setShowEditDialog(true);
        } catch (err) {
          alert('Failed to parse JSON file. Please check the file format.');
          console.error(err);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  return (
    <div className="prompts-container">
      <div className="prompts-header">
        <h3>Prompts</h3>
        {isOwner && (
          <div className="prompts-header-actions">
            <button
              type="button"
              className="import-prompt-button"
              onClick={handleImportFromList}
              disabled={loading}
            >
              <i className="fas fa-file-import"></i> Import
            </button>
            <button
              type="button"
              className="add-prompt-button"
              onClick={handleCreate}
              disabled={loading}
            >
              <i className="fas fa-plus"></i> Add Prompt
            </button>
          </div>
        )}
      </div>

      {error && !showEditDialog && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {loading && !showEditDialog && prompts.length === 0 ? (
        <div className="loading">Loading prompts...</div>
      ) : prompts.length === 0 ? (
        <div className="no-prompts">
          {isOwner ? 'No prompts yet. Click "Add Prompt" to create one.' : 'No prompts available.'}
        </div>
      ) : (
        <div className="prompts-list">
          {prompts.map((prompt) => (
            <div key={prompt.id} className="prompt-item">
              <div className="prompt-header">
                <div className="prompt-title-section">
                  <h4 className={prompt.live ? 'prompt-title' : 'prompt-title draft'}>
                    {prompt.name}{!prompt.live && ' (draft)'}
                  </h4>
                  <span className={`prompt-scope-pill scope-${(prompt.scope || 'Feature Map').toLowerCase().replace(/\s+/g, '-')}`}>
                    {prompt.scope || 'Feature Map'}
                  </span>
                </div>
                {isOwner && (
                  <div className="prompt-actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => handleEdit(prompt)}
                      title="Edit prompt"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      type="button"
                      className="icon-button delete-button"
                      onClick={() => handleDelete(prompt.id)}
                      title="Delete prompt"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="prompt-preview">
                {prompt.prompt_text.length > 200
                  ? prompt.prompt_text.substring(0, 200) + '...'
                  : prompt.prompt_text}
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditDialog && (
        <div className="edit-dialog-overlay" onClick={handleCancel}>
          <div className="edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="edit-dialog-header">
              <h3>{editingPrompt ? 'Edit Prompt' : 'Create Prompt'}</h3>
              <button
                type="button"
                className="close-button"
                onClick={handleCancel}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {error}
              </div>
            )}

            <form onSubmit={handleSave} className="edit-dialog-form">
              <div className="form-group">
                <label htmlFor="prompt-name">
                  Prompt Name <span className="required">*</span>
                </label>
                <input
                  id="prompt-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter prompt name"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="prompt-scope">
                  Scope <span className="required">*</span>
                </label>
                <select
                  id="prompt-scope"
                  value={formData.scope}
                  onChange={(e) => {
                    setFormData({ ...formData, scope: e.target.value });
                    setError(null);
                  }}
                  disabled={loading}
                  required
                >
                  <option value="Feature Map">Feature Map</option>
                  <option value="Feature">Feature</option>
                  <option value="Tag">Tag</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.live}
                    onChange={(e) => {
                      setFormData({ ...formData, live: e.target.checked });
                      setError(null);
                    }}
                    disabled={loading}
                  />
                  <span>Live</span>
                </label>
              </div>

              <div className="form-group">
                <label>Context Data</label>
                <div className="context-options">
                  {CONTEXT_OPTIONS.map(option => (
                    <label key={option} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.context_data?.includes(option) || false}
                        onChange={() => handleContextToggle(option)}
                        disabled={loading}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  {productDetails.length > 0 && (
                    <div className="product-details-options">
                      <div className="product-details-label">Product Details:</div>
                      {productDetails.map(pd => {
                        const optionValue = `${PRODUCT_DETAILS_PREFIX}${pd.document_type}`;
                        return (
                          <label key={pd.id} className="checkbox-label product-detail-checkbox">
                            <input
                              type="checkbox"
                              checked={formData.context_data?.includes(optionValue) || false}
                              onChange={() => handleContextToggle(optionValue)}
                              disabled={loading}
                            />
                            <span>{pd.document_type}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="prompt-text">
                  Prompt Text <span className="required">*</span>
                </label>
                <textarea
                  id="prompt-text"
                  value={formData.prompt_text}
                  onChange={(e) => {
                    setFormData({ ...formData, prompt_text: e.target.value });
                    setError(null);
                  }}
                  placeholder="Enter prompt text"
                  disabled={loading}
                  required
                  rows={15}
                  className="prompt-textarea"
                />
              </div>

              <div className="import-export-actions">
                <button
                  type="button"
                  className="export-button"
                  onClick={handleExport}
                  disabled={loading || !formData.name}
                >
                  <i className="fas fa-file-export"></i> Export
                </button>
              </div>

              <div className="edit-dialog-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i> Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompts;

